import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'

export function PersonnelSyncStatus({
  provider = 'hibob',
}: {
  provider?: 'hibob' | 'adp' | 'successfactors'
}) {
  const name =
    provider === 'successfactors'
      ? 'SAP SuccessFactors'
      : provider === 'adp'
        ? 'ADP'
        : 'HiBob'
  const queryClient = useQueryClient()
  const hibob = useQuery({
    ...trpc.integrations.getHibobSyncStatus.queryOptions(),
    enabled: provider === 'hibob',
    refetchInterval: 5000,
  })
  const adp = useQuery({
    ...trpc.integrations.adp.connectionStatus.queryOptions(),
    enabled: provider === 'adp',
    refetchInterval: 5000,
  })
  const successfactors = useQuery({
    ...trpc.integrations.successfactors.connectionStatus.queryOptions(),
    enabled: provider === 'successfactors',
    refetchInterval: 5000,
  })
  const query =
    provider === 'successfactors'
      ? successfactors
      : provider === 'adp'
        ? adp
        : hibob
  const lastSyncAt = query.data?.lastSyncAt
  useEffect(() => {
    if (!lastSyncAt) return
    void queryClient.invalidateQueries({
      queryKey: trpc.employee.list.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.employee.stats.queryKey(),
    })
  }, [lastSyncAt, queryClient])

  if (query.isError)
    return (
      <p role='alert' className='text-destructive text-sm'>
        Could not load {name} sync status. Retry shortly.
      </p>
    )
  if (query.isPending)
    return (
      <p role='status' className='text-muted-foreground text-sm'>
        Loading {name} sync status…
      </p>
    )
  if (!query.data) return null
  const { lastError, itemsProcessed } = query.data
  const state =
    'jobState' in query.data ? query.data.jobState : query.data.state
  const provisioning =
    provider === 'adp' &&
    'state' in query.data &&
    query.data.state === 'pending_consent'
  const running =
    state === 'waiting' || state === 'active' || state === 'delayed'
  return (
    <div className='space-y-1 text-sm'>
      {running && (
        <p role='status'>
          {name} {provisioning ? 'setup' : 'sync'}{' '}
          {state === 'active' ? 'in progress' : 'queued'}…
        </p>
      )}
      {!running && (lastError || state === 'failed') && (
        <p role='alert' className='text-destructive'>
          {lastError ||
            `${name} ${provisioning ? 'setup' : 'sync'} stopped before completion. Retry ${provisioning ? 'setup in Integrations' : 'sync'}.`}
        </p>
      )}
      {provider === 'adp' &&
        'state' in query.data &&
        query.data.state !== 'active' && (
          <p className='text-muted-foreground'>
            ADP collection is unavailable until setup is complete and the
            subscription is active. Manage the connection in Integrations.
          </p>
        )}
      <p className='text-muted-foreground'>
        {lastSyncAt
          ? `Last successful ${name} sync: ${new Date(lastSyncAt).toLocaleString()} · ${itemsProcessed} employees in the authorized audience.`
          : `No successful ${name} personnel sync yet.`}
      </p>
    </div>
  )
}
